import { dynamicApiSlice } from "../dynamicApi";
import type { WorkflowStepInstanceOutput } from "@/types/WorkflowInstance";
import {
  setOutputApproval,
  addSubmittedFeedback,
  syncOutputFromServer,
  setPendingFeedback,
} from "@/features/workflow/outputStateSlice";

export interface UpdateWorkflowStepInstanceOutputRequest {
  outputValue: string;
  changeReason?: string;
  changeAdditionalInformation?: string;
}

export interface UpdateWorkflowStepInstanceFeedbackRequest {
  feedbackValue: string;
  changeReason?: string;
  changeAdditionalInformation?: string;
  attachments?: string[]; // Array of file IDs
}

export interface EditSelectOptionRequest {
  optionKey: string;
  newValue: string;
  reason?: string;
  additionalInformation?: string;
}

export const workflowStepInstanceOutputsApi = dynamicApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    updateWorkflowStepInstanceOutput: builder.mutation<
      WorkflowStepInstanceOutput,
      {
        workflowInstanceId: string;
        workflowStepInstanceId: string;
        workflowStepInstanceOutputId: string;
        body: UpdateWorkflowStepInstanceOutputRequest;
      }
    >({
      query: ({
        workflowInstanceId,
        workflowStepInstanceId,
        workflowStepInstanceOutputId,
        body,
      }) => ({
        url: `workflow-instance/${workflowInstanceId}/step/${workflowStepInstanceId}/step-output/${workflowStepInstanceOutputId}/update`,
        method: "PUT",
        body,
      }),
      async onQueryStarted(
        { workflowStepInstanceId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedOutput } = await queryFulfilled;

          dispatch(
            syncOutputFromServer({
              stepInstanceId: workflowStepInstanceId,
              output: updatedOutput,
            }),
          );
        } catch (error) {
          // Optimistic update failed, RTK Query will rollback automatically
          console.error("Failed to update output:", error);
        }
      },
    }),
    approveWorkflowStepInstanceOutput: builder.mutation<
      WorkflowStepInstanceOutput,
      {
        workflowInstanceId: string;
        workflowStepInstanceId: string;
        workflowStepInstanceOutputId: string;
      }
    >({
      query: ({
        workflowInstanceId,
        workflowStepInstanceId,
        workflowStepInstanceOutputId,
      }) => ({
        url: `workflow-instance/${workflowInstanceId}/step/${workflowStepInstanceId}/step-output/${workflowStepInstanceOutputId}/approve`,
        method: "POST",
      }),
      async onQueryStarted(
        { workflowStepInstanceId, workflowStepInstanceOutputId },
        { dispatch, queryFulfilled },
      ) {
        // Optimistic update: set approval immediately
        dispatch(
          setOutputApproval({
            stepInstanceId: workflowStepInstanceId,
            outputId: workflowStepInstanceOutputId,
            isApproved: true,
          }),
        );

        try {
          const { data: updatedOutput } = await queryFulfilled;

          dispatch(
            syncOutputFromServer({
              stepInstanceId: workflowStepInstanceId,
              output: updatedOutput,
            }),
          );
        } catch (error) {
          // Rollback on failure: set approval back to false
          dispatch(
            setOutputApproval({
              stepInstanceId: workflowStepInstanceId,
              outputId: workflowStepInstanceOutputId,
              isApproved: false,
            }),
          );
          console.error("Failed to approve output:", error);
        }
      },
    }),
    updateWorkflowStepInstanceFeedback: builder.mutation<
      WorkflowStepInstanceOutput,
      {
        workflowInstanceId: string;
        workflowStepInstanceId: string;
        workflowStepInstanceOutputId: string;
        body: UpdateWorkflowStepInstanceFeedbackRequest;
      }
    >({
      query: ({
        workflowInstanceId,
        workflowStepInstanceId,
        workflowStepInstanceOutputId,
        body,
      }) => ({
        url: `workflow-instance/${workflowInstanceId}/step/${workflowStepInstanceId}/step-output/${workflowStepInstanceOutputId}/feedback`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { workflowInstanceId }) => [
        { type: "WorkflowInstance", id: workflowInstanceId },
        { type: "WorkflowInstance", id: workflowInstanceId.toLowerCase() },
      ],
      async onQueryStarted(
        { workflowStepInstanceId, workflowStepInstanceOutputId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedOutput } = await queryFulfilled;

          // On success, clear pending feedback and mark as submitted
          dispatch(
            setPendingFeedback({
              stepInstanceId: workflowStepInstanceId,
              outputId: workflowStepInstanceOutputId,
              feedbackText: null,
            }),
          );

          dispatch(
            addSubmittedFeedback({
              stepInstanceId: workflowStepInstanceId,
              outputId: workflowStepInstanceOutputId,
            }),
          );

          // Sync server state
          dispatch(
            syncOutputFromServer({
              stepInstanceId: workflowStepInstanceId,
              output: updatedOutput,
            }),
          );
        } catch (error) {
          console.error("Failed to update feedback:", error);
        }
      },
    }),
    editSelectOption: builder.mutation<
      WorkflowStepInstanceOutput,
      {
        workflowInstanceId: string;
        workflowStepInstanceId: string;
        workflowStepInstanceOutputId: string;
        body: EditSelectOptionRequest;
      }
    >({
      query: ({
        workflowInstanceId,
        workflowStepInstanceId,
        workflowStepInstanceOutputId,
        body,
      }) => ({
        url: `workflow-instance/${workflowInstanceId}/step/${workflowStepInstanceId}/step-output/${workflowStepInstanceOutputId}/edit-select-option`,
        method: "PUT",
        body,
      }),
      async onQueryStarted(
        { workflowStepInstanceId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedOutput } = await queryFulfilled;

          dispatch(
            syncOutputFromServer({
              stepInstanceId: workflowStepInstanceId,
              output: updatedOutput,
            }),
          );
        } catch (error) {
          console.error("Failed to edit select option:", error);
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useUpdateWorkflowStepInstanceOutputMutation,
  useApproveWorkflowStepInstanceOutputMutation,
  useUpdateWorkflowStepInstanceFeedbackMutation,
  useEditSelectOptionMutation,
} = workflowStepInstanceOutputsApi;
